import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import BrandNav from '../../components/common/BrandNav';
import { renderSlideToCanvas } from '../../utils/slideRenderer';
import { hasAnyAnimation } from '../../utils/animationCatalog';

// ── Icons ─────────────────────────────────────────────────────────────────────
const IcoPlus = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);
const IcoMenu3 = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <circle cx="10" cy="4"  r="1.5"/><circle cx="10" cy="10" r="1.5"/><circle cx="10" cy="16" r="1.5"/>
  </svg>
);
const IcoHamburger = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <rect x="2" y="4"  width="16" height="2" rx="1"/>
    <rect x="2" y="9"  width="16" height="2" rx="1"/>
    <rect x="2" y="14" width="16" height="2" rx="1"/>
  </svg>
);

// ── Send-to-TV Modal ──────────────────────────────────────────────────────────
function SendToTVModal({ slides, slideIds, brandId, onClose, onSent }) {
  const [devices,      setDevices]      = useState([]);
  const [groups,       setGroups]       = useState([]);
  const [selDevices,   setSelDevices]   = useState({});
  const [selGroups,    setSelGroups]    = useState({});
  const [targetFolder, setTargetFolder] = useState('');
  const [subfolders,   setSubfolders]   = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [sending,      setSending]      = useState(false);
  const [results,      setResults]      = useState(null);
  const [loadingTvs,   setLoadingTvs]   = useState(true);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const firstSlide  = slides.find(s => slideIds.includes(s.id));
  const lastTargets = firstSlide?.send_targets || null;

  // Load devices, groups, then fetch subfolders from first available device.
  // 2026-05-17 — pass brand_id so the backend returns the BRAND OWNER's TVs,
  // not the current viewer's. Without this, an admin opening a brand's slides
  // page would see their own TVs instead of the brand's. (The slide builder's
  // BrandSendToTVModal already does this correctly; this is the slides-list
  // copy that was missing the param.)
  useEffect(() => {
    setLoadingTvs(true);
    const q = brandId ? `?brand_id=${encodeURIComponent(brandId)}` : '';
    Promise.all([
      api.get(`/users/my-devices${q}`),
      api.get(`/users/my-groups${q}`),
    ]).then(([dRes, gRes]) => {
      const devs  = dRes.data || [];
      const grps  = gRes.data || [];
      setDevices(devs);
      setGroups(grps);

      // Pre-fill from last send_targets
      if (lastTargets) {
        const devSel = {};
        (lastTargets.device_names || []).forEach(n => { devSel[n] = true; });
        setSelDevices(devSel);
        const grpSel = {};
        (lastTargets.group_ids || []).forEach(id => { grpSel[id] = true; });
        setSelGroups(grpSel);
        if (lastTargets.target_folder !== undefined) setTargetFolder(lastTargets.target_folder || '');
      }

      // Load subfolders from first device
      const probe = devs[0]?.device_name;
      if (probe) {
        setLoadingFolders(true);
        api.get(`/brand-slides/device-subfolders?device_name=${encodeURIComponent(probe)}`)
          .then(r => setSubfolders(r.data || []))
          .catch(() => setSubfolders([]))
          .finally(() => setLoadingFolders(false));
      }
    }).catch(() => toast.error('Failed to load TVs')).finally(() => setLoadingTvs(false));
  }, []); // eslint-disable-line

  const selDeviceNames = Object.keys(selDevices).filter(k => selDevices[k]);
  const selGroupIds    = Object.keys(selGroups).filter(k => selGroups[k]).map(Number);
  const hasSelection   = selDeviceNames.length > 0 || selGroupIds.length > 0;

  const toggleAllDevices = () => {
    const allOn = devices.every(d => selDevices[d.device_name]);
    const next  = {};
    devices.forEach(d => { next[d.device_name] = !allOn; });
    setSelDevices(next);
  };
  const toggleAllGroups = () => {
    const allOn = groups.every(g => selGroups[g.id]);
    const next  = {};
    groups.forEach(g => { next[g.id] = !allOn; });
    setSelGroups(next);
  };

  const handleSend = async () => {
    if (!hasSelection) { toast.error('Select at least one TV or group'); return; }
    setSending(true);
    setResults(null);
    setProgress({ current: 0, total: slideIds.length });
    let totalSent = 0, totalFailed = 0, allErrors = [];

    for (let i = 0; i < slideIds.length; i++) {
      setProgress({ current: i + 1, total: slideIds.length });
      try {
        const res = await api.post(`/brand-slides/${slideIds[i]}/send`, {
          brand_id:      parseInt(brandId),
          device_names:  selDeviceNames,
          group_ids:     selGroupIds,
          target_folder: targetFolder,
        });
        totalSent   += res.data.success || 0;
        totalFailed += res.data.failed  || 0;
        allErrors    = [...allErrors, ...(res.data.errors || [])];
        if (onSent) onSent(slideIds[i], res.data.send_targets);
      } catch (e) {
        totalFailed++;
        allErrors.push(`Slide ${slideIds[i]}: ${e.response?.data?.error || e.message}`);
      }
    }

    setSending(false);
    setResults({ sent: totalSent, failed: totalFailed, errors: allErrors });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 16, width: 460, maxWidth: '100%', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 48px rgba(0,0,0,0.22)' }}>
        {/* Header */}
        <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid #F0F0F0', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 20 }}>📺</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>Send to TVs</div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 1 }}>
              {slideIds.length} slide{slideIds.length > 1 ? 's' : ''}
              {lastTargets && <span style={{ marginLeft: 6, color: '#1565C0' }}>• last targets pre-filled</span>}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#999', padding: 4 }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px' }}>
          {loadingTvs ? (
            <div style={{ textAlign: 'center', padding: 32, color: '#aaa' }}>Loading TVs…</div>
          ) : results ? (
            /* ── Result view ── */
            <div style={{ padding: '8px 0' }}>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>
                {results.failed === 0 ? '✅' : '⚠️'} Send complete
              </div>
              {results.sent > 0 && (
                <div style={{ color: '#2E7D32', fontSize: 13, marginBottom: 6 }}>
                  ✓ Sent to {results.sent} TV{results.sent > 1 ? 's' : ''} successfully
                </div>
              )}
              {results.failed > 0 && (
                <div style={{ color: '#C62828', fontSize: 13, marginBottom: 6 }}>
                  ✗ Failed: {results.failed} TV{results.failed > 1 ? 's' : ''}
                </div>
              )}
              {results.errors.length > 0 && (
                <div style={{ marginTop: 8, maxHeight: 120, overflowY: 'auto', background: '#FFF5F5', borderRadius: 8, padding: '8px 10px' }}>
                  {results.errors.map((e, i) => (
                    <div key={i} style={{ fontSize: 11, color: '#C62828', marginBottom: 3 }}>• {e}</div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              {/* ── Destination Folder ── */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 6 }}>
                  Destination folder
                  <span style={{ fontWeight: 400, color: '#999', marginLeft: 6 }}>(leave empty for device root)</span>
                </div>
                <input
                  value={targetFolder}
                  onChange={e => setTargetFolder(e.target.value)}
                  placeholder="Root of device"
                  style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1.5px solid #E0E0E0', fontSize: 13, boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' }}
                />
                {/* Subfolder chips */}
                {loadingFolders ? (
                  <div style={{ fontSize: 11, color: '#aaa', marginTop: 6 }}>Loading folders…</div>
                ) : subfolders.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8 }}>
                    {/* "Root" chip to clear selection */}
                    <button
                      onClick={() => setTargetFolder('')}
                      style={{
                        padding: '3px 10px', borderRadius: 20, border: '1.5px solid',
                        borderColor: targetFolder === '' ? '#1565C0' : '#E0E0E0',
                        background:  targetFolder === '' ? '#E3F2FD' : '#F8F9FA',
                        color:       targetFolder === '' ? '#1565C0' : '#666',
                        fontSize: 12, cursor: 'pointer', fontWeight: targetFolder === '' ? 700 : 400,
                      }}
                    >
                      📁 Root
                    </button>
                    {subfolders.map(f => (
                      <button
                        key={f}
                        onClick={() => setTargetFolder(f)}
                        style={{
                          padding: '3px 10px', borderRadius: 20, border: '1.5px solid',
                          borderColor: targetFolder === f ? '#1565C0' : '#E0E0E0',
                          background:  targetFolder === f ? '#E3F2FD' : '#F8F9FA',
                          color:       targetFolder === f ? '#1565C0' : '#555',
                          fontSize: 12, cursor: 'pointer', fontWeight: targetFolder === f ? 700 : 400,
                        }}
                      >
                        📂 {f}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>

              {/* ── Groups ── */}
              {groups.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>Groups</div>
                    <button onClick={toggleAllGroups} style={{ fontSize: 11, color: '#1565C0', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}>
                      {groups.every(g => selGroups[g.id]) ? 'Deselect all' : 'Select all'}
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {groups.map(g => (
                      <label key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '6px 10px', borderRadius: 8, cursor: 'pointer', background: selGroups[g.id] ? '#E3F2FD' : '#F8F9FA', border: `1.5px solid ${selGroups[g.id] ? '#90CAF9' : '#E8E8E8'}`, transition: 'all 0.1s' }}>
                        <input type="checkbox" checked={!!selGroups[g.id]} onChange={e => setSelGroups(p => ({ ...p, [g.id]: e.target.checked }))} style={{ accentColor: '#1565C0', width: 15, height: 15, flexShrink: 0 }} />
                        <span style={{ width: 10, height: 10, borderRadius: 3, background: g.color || '#2196F3', flexShrink: 0 }} />
                        <span style={{ fontSize: 13, fontWeight: 500, flex: 1 }}>{g.name}</span>
                        <span style={{ fontSize: 11, color: '#999' }}>{(g.device_names || []).length} TVs</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Individual TVs ── */}
              {devices.length > 0 && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>Individual TVs</div>
                    <button onClick={toggleAllDevices} style={{ fontSize: 11, color: '#1565C0', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}>
                      {devices.every(d => selDevices[d.device_name]) ? 'Deselect all' : 'Select all'}
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {devices.map(d => (
                      <label key={d.device_name} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '6px 10px', borderRadius: 8, cursor: 'pointer', background: selDevices[d.device_name] ? '#E3F2FD' : '#F8F9FA', border: `1.5px solid ${selDevices[d.device_name] ? '#90CAF9' : '#E8E8E8'}`, transition: 'all 0.1s' }}>
                        <input type="checkbox" checked={!!selDevices[d.device_name]} onChange={e => setSelDevices(p => ({ ...p, [d.device_name]: e.target.checked }))} style={{ accentColor: '#1565C0', width: 15, height: 15, flexShrink: 0 }} />
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: d.is_online ? '#4CAF50' : '#ccc', flexShrink: 0 }} />
                        <span style={{ fontSize: 13, flex: 1 }}>{d.device_name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {devices.length === 0 && groups.length === 0 && (
                <div style={{ textAlign: 'center', padding: 24, color: '#aaa', fontSize: 13 }}>No TVs or groups found for your account.</div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid #F0F0F0', display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
          {sending && (
            <span style={{ fontSize: 12, color: '#888', marginRight: 'auto' }}>
              Sending {progress.current}/{progress.total}…
            </span>
          )}
          {results ? (
            <button onClick={onClose} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#2196F3', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>Done</button>
          ) : (
            <>
              <button onClick={onClose} disabled={sending} style={{ padding: '8px 18px', borderRadius: 8, border: '1.5px solid #E0E0E0', background: '#fff', cursor: 'pointer', fontSize: 13, color: '#555' }}>Cancel</button>
              <button onClick={handleSend} disabled={sending || !hasSelection || loadingTvs}
                style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: hasSelection ? '#1565C0' : '#E0E0E0', color: hasSelection ? '#fff' : '#aaa', cursor: hasSelection ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 7 }}>
                {sending ? '⏳ Sending…' : '📺 Send Now'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Card 3-dot menu ───────────────────────────────────────────────────────────
function CardMenu({ slide, brandId, navigate, isTemplate, onPreview, onUpdate, onSendTV, onDownload, onBackup, onRestore, onDuplicate, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const action = (fn) => { setOpen(false); fn(); };

  const hasSendTargets = !!slide.send_targets &&
    ((slide.send_targets.device_names?.length > 0) || (slide.send_targets.group_ids?.length > 0));

  return (
    <div ref={ref} style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
      <button
        onClick={() => setOpen(p => !p)}
        style={{ width: 28, height: 28, borderRadius: 7, border: '1.5px solid rgba(255,255,255,0.7)', background: 'rgba(0,0,0,0.35)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}
      >
        <IcoMenu3 />
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 300, background: '#fff', borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.18)', border: '1px solid #E8E8E8', minWidth: 150, padding: '4px 0' }}>
          {onPreview && <button onClick={() => action(onPreview)} style={menuItemStyle()}>👁 Preview</button>}
          {onPreview && <div style={{ height: 1, background: '#F0F0F0', margin: '3px 0' }} />}
          {/* Render / Download / To TV all move a PICTURE of the slide onto a
              TV. An info-screen template is never sent that way — the Info
              Screen publishes a live HTML page from it — so offering these
              here would be three buttons that quietly do the wrong thing. */}
          {!isTemplate && <button onClick={() => action(onUpdate)} style={menuItemStyle()}>🔄 Update</button>}
          {!isTemplate && (
            <button onClick={() => action(onSendTV)} style={menuItemStyle()}>
              📺 To TV{hasSendTargets ? ' ✓' : ''}
            </button>
          )}
          {!isTemplate && <button onClick={() => action(onDownload)} style={menuItemStyle()}>⬇️ Download</button>}
          <button onClick={() => action(onDuplicate)} style={menuItemStyle()}>⧉ Duplicate</button>
          <div style={{ height: 1, background: '#F0F0F0', margin: '3px 0' }} />
          <button onClick={() => action(onBackup)} style={menuItemStyle()}>💾 Backup</button>
          <button onClick={() => action(onRestore)} style={menuItemStyle()}>♻️ Restore</button>
          <div style={{ height: 1, background: '#F0F0F0', margin: '3px 0' }} />
          <button onClick={() => action(onDelete)} style={menuItemStyle(true)}>🗑 Delete</button>
        </div>
      )}
    </div>
  );
}
function menuItemStyle(danger = false) {
  return {
    display: 'flex', alignItems: 'center', gap: 8,
    width: '100%', padding: '8px 13px', border: 'none',
    background: 'transparent', cursor: 'pointer',
    fontSize: 13, color: danger ? '#C62828' : '#333',
    textAlign: 'left',
  };
}

// ── ThumbCard ─────────────────────────────────────────────────────────────────
function ThumbCard({
  slide, brandId, navigate, isTemplate, backFrom,
  selected, onToggleSelect,
  renaming, renameVal, renameRef,
  onRenameStart, onRenameChange, onRenameSubmit, onRenameCancel,
  onPreview, onUpdate, onSendTV, onDownload, onBackup, onRestore, onDuplicate, onDelete,
}) {
  const isSel  = selected.has(slide.id);
  const isV    = slide.orientation === 'V';
  const hasImg = !!slide.thumb_path;
  const thumbSrc = slide.thumb_path
    ? `${slide.thumb_path}?v=${new Date(slide.updated_at || 0).getTime()}`
    : null;
  const cols = slide.zone_cols || 1;
  const rows = slide.zone_rows || 1;
  // Phase 5: card bg = checker pattern when the slide itself is transparent (bg_color empty/'transparent'),
  // so the user can see the alpha rather than mistaking a UI-card-color for the slide's bg.
  const isTransparent = !slide.bg_color || slide.bg_color === 'transparent' || slide.bg_color === 'none';
  const transparentStyle = {
    backgroundColor: '#fff',
    backgroundImage: 'linear-gradient(45deg,#ddd 25%,transparent 25%),linear-gradient(-45deg,#ddd 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#ddd 75%),linear-gradient(-45deg,transparent 75%,#ddd 75%)',
    backgroundSize: '14px 14px',
    backgroundPosition: '0 0, 0 7px, 7px -7px, -7px 0',
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Thumbnail */}
      <div
        onClick={() => navigate(`/brands/${brandId}/slide-builder/${slide.id}?from=${backFrom}`)}
        style={{
          ...(isTransparent ? transparentStyle : { background: hasImg ? '#000' : (slide.bg_color || '#111827') }),
          borderRadius: 12, overflow: 'hidden', cursor: 'pointer',
          border: isSel ? '3px solid #2196F3' : '2px solid #E0E0E0',
          boxShadow: isSel ? '0 0 0 3px #90CAF980' : '0 2px 10px rgba(0,0,0,0.09)',
          transition: 'all 0.15s',
          aspectRatio: isV ? '9 / 16' : '16 / 9',
          position: 'relative',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {hasImg ? (
          <img src={thumbSrc} alt={slide.slide_name}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : (
          <div style={{ width: '75%', height: '75%', display: 'grid', gridTemplateColumns: `repeat(${cols},1fr)`, gridTemplateRows: `repeat(${rows},1fr)`, gap: 3, padding: 4, boxSizing: 'border-box' }}>
            {Array.from({ length: cols * rows }).map((_, i) => (
              <div key={i} style={{ background: `${slide.accent_color||'#F59E0B'}44`, borderRadius: 3, border: `1px solid ${slide.accent_color||'#F59E0B'}66` }} />
            ))}
          </div>
        )}

        {/* Checkbox — bottom-left */}
        <div onClick={e => onToggleSelect(e, slide.id)}
          style={{ position: 'absolute', bottom: 8, left: 8, cursor: 'pointer' }}>
          <div style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${isSel ? '#2196F3' : 'rgba(255,255,255,0.7)'}`, background: isSel ? '#2196F3' : 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}>
            {isSel && <span style={{ color: '#fff', fontSize: 13, fontWeight: 700, lineHeight: 1 }}>✓</span>}
          </div>
        </div>

        {/* 2026-05-16 — kind badge top-left so video slides are scannable
            from the grid without clicking in. */}
        {isTemplate ? (
          <div style={{ position: 'absolute', top: 7, left: 7, background: '#0F766ECC', borderRadius: 5, padding: '2px 7px', fontSize: 10, color: '#fff', fontWeight: 700, letterSpacing: 0.4 }}>🪧 TEMPLATE</div>
        ) : slide.slide_kind === 'video' ? (
          <div style={{ position: 'absolute', top: 7, left: 7, background: '#6D28D9CC', borderRadius: 5, padding: '2px 7px', fontSize: 10, color: '#fff', fontWeight: 700, letterSpacing: 0.4 }}>🎬 VIDEO</div>
        ) : (
          <div style={{ position: 'absolute', top: 7, left: 7, background: 'rgba(0,0,0,0.45)', borderRadius: 5, padding: '2px 7px', fontSize: 10, color: '#fff', fontWeight: 700, letterSpacing: 0.4 }}>🖼 IMG</div>
        )}

        {/* Send targets indicator — top-right */}
        {slide.send_targets && ((slide.send_targets.device_names?.length > 0) || (slide.send_targets.group_ids?.length > 0)) && (
          <div style={{ position: 'absolute', top: 7, right: 7, background: '#1565C0CC', borderRadius: 5, padding: '2px 6px', fontSize: 10, color: '#fff', fontWeight: 700 }}>📺</div>
        )}
      </div>

      {/* 3-dot menu — outside overflow:hidden, bottom-right of outer wrapper */}
      <div style={{ position: 'absolute', bottom: 8, right: 8 }} onClick={e => e.stopPropagation()}>
        <CardMenu
          slide={slide} brandId={brandId} navigate={navigate} isTemplate={isTemplate}
          onPreview={onPreview ? () => onPreview(slide.id) : undefined}
          onUpdate={() => onUpdate(slide.id)}
          onSendTV={() => onSendTV(slide.id)}
          onDownload={() => onDownload(slide.id)}
          onBackup={() => onBackup(slide.id)}
          onRestore={() => onRestore(slide.id)}
          onDuplicate={() => onDuplicate(slide.id)}
          onDelete={() => onDelete(slide.id)}
        />
      </div>

      {/* Slide name */}
      {renaming === slide.id ? (
        <input
          ref={renameRef}
          value={renameVal}
          onChange={e => onRenameChange(e.target.value)}
          onBlur={() => onRenameSubmit(slide.id)}
          onKeyDown={e => { if (e.key === 'Enter') onRenameSubmit(slide.id); if (e.key === 'Escape') onRenameCancel(); }}
          onClick={e => e.stopPropagation()}
          style={{ width: '100%', marginTop: 8, padding: '4px 8px', fontSize: 13, borderRadius: 6, border: '1.5px solid #2196F3', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
        />
      ) : (
        <div
          onClick={e => onRenameStart(e, slide)}
          title="Click to rename"
          style={{ marginTop: 8, fontSize: 13, fontWeight: 500, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'text', padding: '2px 4px', borderRadius: 4, transition: 'background 0.15s' }}
          onMouseOver={e => e.currentTarget.style.background = '#F0F7FF'}
          onMouseOut={e => e.currentTarget.style.background = 'transparent'}
        >
          {slide.slide_name || 'Untitled'}
        </div>
      )}
    </div>
  );
}

// ── SlideManager ──────────────────────────────────────────────────────────────
export default function SlideManager() {
  const { brandId } = useParams();
  useEffect(() => { if (brandId) localStorage.setItem('pm_last_brand', brandId); }, [brandId]);
  const navigate = useNavigate();

  // Two kinds of slide live in this table and they are not interchangeable:
  // menu slides get rendered to a picture and pushed to a TV, info-screen
  // templates get published as a live HTML page by an Info Screen. Same editor,
  // different destination — so the page says which one you are looking at
  // instead of leaving the user to guess from the contents.
  // The mode rides in the URL so a refresh, a bookmark, and the builder's back
  // button all land on the tab you were actually in.
  const [sp, setSp] = useSearchParams();
  const kind = sp.get('kind') === 'doorsign' ? 'doorsign' : 'products';
  const isTemplateMode = kind === 'doorsign';
  const backFrom = isTemplateMode ? 'templates' : 'slides';
  const setKind = (k) => {
    setSelected(new Set());
    const next = new URLSearchParams(sp);
    if (k === 'doorsign') next.set('kind', 'doorsign'); else next.delete('kind');
    setSp(next, { replace: true });
  };

  const [brand,          setBrand]          = useState(null);
  const [slides,         setSlides]         = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [selected,       setSelected]       = useState(new Set());
  const [renaming,       setRenaming]       = useState(null);
  const [renameVal,      setRenameVal]      = useState('');
  // English is the default on every entry and the app stays LTR until the
  // user presses the language button — the choice is deliberately not persisted.
  const [lang,           setLang]           = useState('en');
  const [updating,       setUpdating]       = useState(false);
  const [updateProgress, setUpdateProgress] = useState({ current: 0, total: 0 });
  const [showPageMenu,    setShowPageMenu]   = useState(false);
  const [sendModal,       setSendModal]      = useState(null); // {slideIds:[]}
  const [zipProgress,     setZipProgress]    = useState(null); // null|{current,total,phase}
  const [restoreTargetId, setRestoreTargetId] = useState(null);
  const [previewSlideId, setPreviewSlideId] = useState(null);
  const restoreFileRef    = useRef(null); // hamburger restore (multiple files)
  const restoreCardRef    = useRef(null); // card restore (single file)
  const renameRef   = useRef(null);
  const pageMenuRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [bRes, sRes] = await Promise.all([
        api.get(`/brands/${brandId}`),
        api.get(`/brand-slides?brand_id=${brandId}&kind=${kind}`),
      ]);
      setBrand(bRes.data);
      const sorted = (sRes.data || []).slice().sort((a, b) =>
        (a.slide_name || '').toLowerCase().localeCompare((b.slide_name || '').toLowerCase())
      );
      setSlides(sorted);
    } catch { toast.error('Failed to load slides'); }
    finally { setLoading(false); }
  }, [brandId, kind]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (renaming) setTimeout(() => renameRef.current?.focus(), 50);
  }, [renaming]);

  // ESC closes the fullscreen preview
  useEffect(() => {
    if (!previewSlideId) return;
    const onKey = e => { if (e.key === 'Escape') setPreviewSlideId(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [previewSlideId]);

  // Close page menu on outside click
  useEffect(() => {
    const h = e => { if (pageMenuRef.current && !pageMenuRef.current.contains(e.target)) setShowPageMenu(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // ── Select ────────────────────────────────────────────────────────────────
  const toggleSelect = (e, id) => {
    e.stopPropagation();
    setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };
  const toggleSelectAll = () => {
    if (selected.size === slides.length) setSelected(new Set());
    else setSelected(new Set(slides.map(s => s.id)));
  };
  const allSelected = slides.length > 0 && selected.size === slides.length;
  const someSelected = selected.size > 0 && selected.size < slides.length;

  // ── Rename ────────────────────────────────────────────────────────────────
  const startRename  = (e, slide) => { e.stopPropagation(); setRenameVal(slide.slide_name || ''); setRenaming(slide.id); };
  const submitRename = async (id) => {
    const trimmed = renameVal.trim(); setRenaming(null);
    if (!trimmed) return;
    try {
      await api.put(`/brand-slides/${id}/rename`, { slide_name: trimmed });
      setSlides(prev => prev.map(s => s.id === id ? { ...s, slide_name: trimmed } : s));
      toast.success('Renamed!');
    } catch { toast.error('Rename failed'); }
  };
  const cancelRename = () => setRenaming(null);

  // ── Duplicate ─────────────────────────────────────────────────────────────
  const handleDuplicate = async (slideId) => {
    try {
      const res = await api.post(`/brand-slides/${slideId}/duplicate`);
      setSlides(prev => [...prev, res.data]);
      toast.success('Duplicated!');
    } catch { toast.error('Duplicate failed'); }
  };

  // ── Download ──────────────────────────────────────────────────────────────
  // 2026-05-16: video slides (jpeg_path = .mp4) need a fallback path because
  // some HEAD configurations / CORS edges make fetch().blob() reject. If the
  // fetch path fails, fall back to a direct anchor click — same-origin URLs
  // honour the `download` attribute and the browser handles the rest.
  const handleDownload = async (slideId) => {
    const slide = slides.find(s => s.id === slideId);
    if (!slide?.jpeg_path) { toast.error('Slide not saved yet — update it first'); return; }
    const filename = slide.jpeg_path.split('/').pop().replace(/\?.*$/, '');
    const cb = slide.jpeg_path.includes('?') ? '&' : '?';
    const url = `${slide.jpeg_path}${cb}v=${new Date(slide.updated_at || 0).getTime()}`;
    try {
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob   = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a      = document.createElement('a');
      a.href = objUrl; a.download = filename; a.click();
      URL.revokeObjectURL(objUrl);
    } catch (err) {
      console.warn('[download] fetch+blob failed, falling back to direct anchor:', err);
      try {
        const a = document.createElement('a');
        a.href = url; a.download = filename; a.target = '_self';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (err2) {
        console.error('[download] direct anchor also failed:', err2);
        window.open(url, '_blank', 'noopener');
        toast.error('Download blocked by browser — opened in new tab');
      }
    }
  };

  const handleBulkDownload = async () => {
    const ids        = selected.size > 0 ? [...selected] : slides.map(s => s.id);
    const toDownload = ids.map(id => slides.find(s => s.id === id)).filter(s => s?.jpeg_path);
    if (toDownload.length === 0) { toast.error('No saved slides to download'); return; }
    setShowPageMenu(false);
    setZipProgress({ current: 0, total: toDownload.length, phase: 'fetching' });
    const JSZip = (await import('jszip')).default;
    const zip   = new JSZip();
    let failed  = 0;
    for (let i = 0; i < toDownload.length; i++) {
      const slide = toDownload[i];
      setZipProgress({ current: i + 1, total: toDownload.length, phase: 'fetching' });
      try {
        const cb = slide.jpeg_path.includes('?') ? '&' : '?';
        const url = `${slide.jpeg_path}${cb}v=${new Date(slide.updated_at || 0).getTime()}`;
        const res      = await fetch(url, { cache: 'no-store' });
        const blob     = await res.blob();
        const filename = slide.jpeg_path.split('/').pop().replace(/\?.*$/, '');
        zip.file(filename, blob);
      } catch { failed++; }
    }
    setZipProgress({ current: toDownload.length, total: toDownload.length, phase: 'zipping' });
    try {
      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const a   = document.createElement('a');
      a.href = url;
      a.download = `slides-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(failed === 0
        ? `${toDownload.length} slides downloaded as ZIP ✓`
        : `${toDownload.length - failed} in ZIP, ${failed} failed`);
    } catch { toast.error('ZIP creation failed'); }
    setZipProgress(null);
  };

  // ── Backup / Restore ──────────────────────────────────────────────────────
  // text_layers / zone_mode / size / sign_fields were missing, so a restored
  // slide came back without its text and a restored template without its
  // fields. menu_kind keeps a template a template. Older backup files simply
  // lack the new keys and fall back to the same defaults as before.
  const BACKUP_FIELDS = ['slide_name','orientation','zone_cols','zone_rows','bg_color','accent_color','slide_bg_overlay','zones',
                         'text_layers','zone_mode','width_px','height_px','lang','menu_kind','sign_fields'];
  const slideToBackupJson = (slide) => JSON.stringify({
    _type: 'slide-backup', _version: 1,
    backed_up_at: new Date().toISOString(),
    ...Object.fromEntries(BACKUP_FIELDS.map(k => [k, slide[k] ?? null])),
    zones: (slide.zones || []).map(z => ({ ...z, bg_image: '' })), // strip image URLs
    slide_bg_image: '',
  }, null, 2);

  const triggerJsonDownload = (content, filename) => {
    const url = URL.createObjectURL(new Blob([content], { type: 'application/json' }));
    const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const safeName = (s) => (s.slide_name || `slide-${s.id}`).replace(/[<>:"/\\|?*]/g, '_');

  const handleBackup = (slideId) => {
    const slide = slides.find(s => s.id === slideId);
    if (!slide) return;
    triggerJsonDownload(slideToBackupJson(slide), `${safeName(slide)}.json`);
    toast.success('Backup saved!');
  };

  // A template has to be BORN a template: the builder's save does not send
  // menu_kind, so a row created blank would land in the menu slides and vanish
  // from Info Screens. Create it here with its kind, then open the designer.
  const handleNew = async () => {
    if (!isTemplateMode) { navigate(`/brands/${brandId}/slide-builder/new?from=slides`); return; }
    const name = window.prompt('Template name', 'Info Screen');
    if (!name) return;
    try {
      const r = await api.post('/brand-slides', {
        brand_id: parseInt(brandId, 10),
        slide_name: name,
        menu_kind: 'doorsign',
        // Doors are portrait far more often than not; still editable inside.
        orientation: 'V', width_px: 720, height_px: 1280,
        zone_mode: 'free', zones: [], text_layers: [],
      });
      navigate(`/brands/${brandId}/slide-builder/${r.data.id}?from=templates`);
    } catch (e) { toast.error(e.response?.data?.error || 'Create failed'); }
  };

  const handleBulkBackup = async () => {
    const ids      = selected.size > 0 ? [...selected] : slides.map(s => s.id);
    const toBak    = ids.map(id => slides.find(s => s.id === id)).filter(Boolean);
    if (!toBak.length) return;
    setShowPageMenu(false);
    if (toBak.length === 1) { handleBackup(toBak[0].id); return; }
    setZipProgress({ current: 0, total: toBak.length, phase: 'fetching' });
    const JSZip = (await import('jszip')).default;
    const zip   = new JSZip();
    for (let i = 0; i < toBak.length; i++) {
      const s = toBak[i];
      setZipProgress({ current: i + 1, total: toBak.length, phase: 'fetching' });
      zip.file(`${safeName(s)}.json`, slideToBackupJson(s));
    }
    setZipProgress({ current: toBak.length, total: toBak.length, phase: 'zipping' });
    const blob = await zip.generateAsync({ type: 'blob' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `slides-backup-${new Date().toISOString().slice(0,10)}.zip`; a.click();
    URL.revokeObjectURL(url);
    setZipProgress(null);
    toast.success(`${toBak.length} slides backed up!`);
  };

  const parseBackupData = async (files) => {
    const all = [];
    const JSZip = (await import('jszip')).default;
    for (const file of files) {
      if (file.name.toLowerCase().endsWith('.zip')) {
        // Extract every .json inside the zip
        const zip = await JSZip.loadAsync(file);
        const jsonFiles = Object.values(zip.files).filter(f => !f.dir && f.name.toLowerCase().endsWith('.json'));
        for (const zf of jsonFiles) {
          try {
            const text   = await zf.async('string');
            const parsed = JSON.parse(text);
            const items  = Array.isArray(parsed) ? parsed : [parsed];
            all.push(...items.filter(d => d.zones !== undefined || d._type === 'slide-backup'));
          } catch { /* skip bad entry */ }
        }
      } else {
        const text   = await file.text();
        const parsed = JSON.parse(text);
        const items  = Array.isArray(parsed) ? parsed : [parsed];
        all.push(...items.filter(d => d.zones !== undefined || d._type === 'slide-backup'));
      }
    }
    return all;
  };

  // Hamburger restore: recreate slide(s) as new slides for this brand
  const handleRestoreFiles = async (files) => {
    if (!files?.length) return;
    let items = [];
    try { items = await parseBackupData(Array.from(files)); } catch { toast.error('Invalid backup file'); return; }
    if (!items.length) { toast.error('No valid slide data found'); return; }
    const toastId = toast.loading(`Restoring ${items.length} slide${items.length > 1 ? 's' : ''}…`);
    let created = 0, failed = 0;
    for (const d of items) {
      try {
        const res = await api.post('/brand-slides', {
          brand_id: parseInt(brandId),
          slide_name:       d.slide_name || 'Restored Slide',
          orientation:      d.orientation || 'H',
          zone_cols:        d.zone_cols   || 1,
          zone_rows:        d.zone_rows   || 1,
          bg_color:         d.bg_color    || '#111827',
          accent_color:     d.accent_color || '#F59E0B',
          slide_bg_image:   '',
          slide_bg_overlay: d.slide_bg_overlay ?? 50,
          zones:            (d.zones || []).map(z => ({ ...z, bg_image: '' })),
          text_layers:      d.text_layers || [],
          zone_mode:        d.zone_mode === 'free' ? 'free' : 'grid',
          ...(d.width_px  ? { width_px:  d.width_px  } : {}),
          ...(d.height_px ? { height_px: d.height_px } : {}),
          // A backup restored while the Templates tab is open belongs in that
          // tab; the file's own kind wins when it has one.
          menu_kind:        d.menu_kind || kind,
        });
        // POST does not insert sign_fields — a template restored without them
        // would come back with no fields to fill in, which is most of what a
        // template IS. The dedicated schema endpoint puts them back.
        if (Array.isArray(d.sign_fields) && d.sign_fields.length) {
          try { await api.put(`/brand-slides/${res.data.id}/sign-schema`, { sign_fields: d.sign_fields }); }
          catch { toast.error(`${d.slide_name || 'Template'}: fields not restored`); }
        }
        setSlides(prev => [...prev, res.data]);
        created++;
      } catch { failed++; }
    }
    toast.dismiss(toastId);
    if (failed === 0) toast.success(`✅ ${created} slide${created > 1 ? 's' : ''} restored!`);
    else toast.error(`${created} restored, ${failed} failed`);
  };

  // Card restore: overwrite a specific slide with backup data
  const handleRestoreToSlide = async (slideId, file) => {
    let d;
    try {
      const items = await parseBackupData([file]);
      d = items[0];
    } catch { toast.error('Invalid backup file'); return; }
    if (!d) { toast.error('No valid slide data in file'); return; }
    try {
      const res = await api.put(`/brand-slides/${slideId}`, {
        slide_name:       d.slide_name || 'Restored Slide',
        orientation:      d.orientation || 'H',
        zone_cols:        d.zone_cols   || 1,
        zone_rows:        d.zone_rows   || 1,
        bg_color:         d.bg_color    || '#111827',
        accent_color:     d.accent_color || '#F59E0B',
        slide_bg_image:   '',
        slide_bg_overlay: d.slide_bg_overlay ?? 50,
        zones:            (d.zones || []).map(z => ({ ...z, bg_image: '' })),
      });
      setSlides(prev => prev.map(s => s.id === slideId ? { ...s, ...res.data } : s));
      toast.success('Slide restored from backup!');
    } catch { toast.error('Restore failed'); }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (slideId) => {
    if (!window.confirm('Delete this slide?')) return;
    try {
      await api.delete(`/brand-slides/${slideId}`);
      setSlides(prev => prev.filter(s => s.id !== slideId));
      setSelected(prev => { const n = new Set(prev); n.delete(slideId); return n; });
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };
  const handleBulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Delete ${selected.size} slide(s)?`)) return;
    try {
      await Promise.all([...selected].map(id => api.delete(`/brand-slides/${id}`)));
      setSlides(prev => prev.filter(s => !selected.has(s.id)));
      setSelected(new Set());
      toast.success('Deleted!');
    } catch { toast.error('Delete failed'); }
  };

  // ── Update (re-render JPEG) ───────────────────────────────────────────────
  const runUpdate = async (slideIds) => {
    if (updating) return;
    setUpdating(true);
    setUpdateProgress({ current: 0, total: slideIds.length });
    let failed = 0;
    try {
      const [mdRes, bRes] = await Promise.all([
        api.get(`/menuboard/menu-data/${brandId}`),
        api.get(`/brands/${brandId}`),
      ]);
      const menuData   = mdRes.data;
      const restaurant = bRes.data;
      for (let i = 0; i < slideIds.length; i++) {
        const slide = slides.find(s => s.id === slideIds[i]);
        if (!slide) continue;
        setUpdateProgress({ current: i + 1, total: slideIds.length });
        try {
          // 2026-05-17 — use the slide's SAVED lang (en/ar). The page-level
          // `lang` state was overwriting per-slide language when re-rendering
          // from the slides list, so a slide saved as Arabic would update as
          // English (or vice versa). Falls back to the page lang only when
          // the slide row doesn't carry a valid value.
          const slideLang = (slide.lang === 'en' || slide.lang === 'ar') ? slide.lang : lang;
          // 2026-05-18 — branch on slide_kind. For VIDEO slides, calling
          // /save-jpeg would overwrite the good thumb_path (which the compose
          // pipeline set to a frame extracted from the rendered MP4) with a
          // transparent overlay-only PNG. Instead, re-trigger /compose-video
          // so the MP4 + its frame thumbnail are regenerated.
          //
          // 2026-05-18 (b) — bug-fix: we must collect BOTH animated text
          // layers AND animated zones. excludeAnimated:true drops them from
          // the base render, so anything we don't ship in anim_layers is
          // missing from the final MP4. This is exactly the helper logic
          // that SlideBuilder.renderAnimatedLayers uses on save — inlined
          // here so the Update-All flow produces the same MP4 the Save
          // button does.
          let res;
          if (slide.slide_kind === 'video' && slide.video_path) {
            const dur = slide.duration_sec || 15;
            const baseCanvas = await renderSlideToCanvas(
              slide, slide.orientation || 'H', menuData, restaurant, slideLang,
              { excludeAnimated: true }
            );
            const baseImg = baseCanvas.toDataURL('image/webp', 0.92);

            const zones      = Array.isArray(slide.zones)       ? slide.zones       : [];
            const textLayers = Array.isArray(slide.text_layers) ? slide.text_layers : [];
            // Same order SlideBuilder.renderAnimatedLayers uses: animated
            // zones first (drawn under text in the normal stack), then
            // animated text layers on top.
            const animItems = [
              ...zones     .filter(z => hasAnyAnimation(z, dur)),
              ...textLayers.filter(t => hasAnyAnimation(t, dur)),
            ];
            const animLayers = [];
            for (const item of animItems) {
              if (!item.id) continue;
              try {
                const layerCanvas = await renderSlideToCanvas(
                  slide, slide.orientation || 'H', menuData, restaurant, slideLang,
                  { onlyLayerId: item.id }
                );
                animLayers.push({
                  image_data:  layerCanvas.toDataURL('image/webp', 0.92),
                  layer_id:    item.id,
                  time_in:     Number.isFinite(item.time_in)  ? item.time_in  : 0,
                  time_out:    Number.isFinite(item.time_out) ? item.time_out : dur,
                  effect_in:   item.effect_in   || 'none',
                  effect_stay: item.effect_stay || 'none',
                  effect_out:  item.effect_out  || 'none',
                  fade_in_dur:  item.fade_in_dur  ?? 0.5,
                  fade_out_dur: item.fade_out_dur ?? 0.5,
                });
              } catch (e) { /* skip this layer if its isolated render fails */ }
            }
            res = await api.post(`/brand-slides/${slide.id}/compose-video`, {
              image_data:  baseImg,
              brand_id:    parseInt(brandId),
              anim_layers: animLayers,
            });
          } else {
            const canvas    = await renderSlideToCanvas(slide, slide.orientation || 'H', menuData, restaurant, slideLang);
            const imageData = canvas.toDataURL('image/webp', 0.92);
            res = await api.post(`/brand-slides/${slide.id}/save-jpeg`, { image_data: imageData, brand_id: parseInt(brandId) });
          }
          setSlides(prev => prev.map(s => s.id === slide.id ? { ...s, ...res.data } : s));
        } catch { failed++; }
      }
      if (failed === 0) toast.success(`${slideIds.length} slide${slideIds.length > 1 ? 's' : ''} updated! ✓`);
      else toast.error(`${slideIds.length - failed} updated, ${failed} failed`);
    } catch (e) { toast.error('Update failed: ' + e.message); }
    finally { setUpdating(false); setUpdateProgress({ current: 0, total: 0 }); }
  };

  const handleUpdateAll      = () => { setShowPageMenu(false); runUpdate(slides.map(s => s.id)); };
  const handleUpdateSelected = () => { setShowPageMenu(false); runUpdate([...selected]); };
  const handleUpdateOne      = (slideId) => runUpdate([slideId]);

  // ── Send to TV ────────────────────────────────────────────────────────────
  const openSendModal = (slideIds) => { setSendModal({ slideIds }); setShowPageMenu(false); };
  const onSendComplete = (slideId, sendTargets) => {
    setSlides(prev => prev.map(s => s.id === slideId ? { ...s, send_targets: sendTargets } : s));
  };

  // ── "To TV (last choice)" — sends without opening modal if targets saved ──
  const handleQuickSend = async (slideIds) => {
    // 2026-05-18 — owner-reported bug fix. The original implementation
    // collected the UNION of every selected slide's last targets and then
    // sent EVERY slide to that combined target set. With slide-1→tv-A,
    // slide-2→tv-B, slide-3→tv-C, the union {A,B,C} got every slide pushed
    // to every TV, and on completion each slide's send_targets row was
    // overwritten to all three. Per-slide assignment was destroyed.
    //
    // New logic:
    //   • Each slide goes to ITS OWN last_targets, not the union.
    //   • If any slide has no saved targets, open the modal for ALL slides
    //     (we don't half-send some via quick path + open a modal for others).
    //   • Slides with empty targets are skipped (counted as failed).
    const planned = slideIds.map(id => {
      const s = slides.find(sl => sl.id === id);
      const t = s?.send_targets;
      const devices = (t?.device_names || []);
      const groups  = (t?.group_ids    || []);
      return {
        id,
        slide: s,
        device_names:  devices,
        group_ids:     groups,
        target_folder: t?.target_folder || '',
        hasAny: !!t && (devices.length > 0 || groups.length > 0),
      };
    });
    const anyMissing = planned.some(p => !p.hasAny);
    if (anyMissing) {
      // No usable saved targets for at least one slide → fall back to the
      // modal so the user can pick destinations explicitly. Pre-fill is
      // handled by the modal itself using firstSlide.send_targets.
      openSendModal(slideIds);
      return;
    }

    setShowPageMenu(false);
    const toastId = toast.loading(`Sending ${slideIds.length} slide${slideIds.length > 1 ? 's' : ''}…`);
    let totalSent = 0, totalFailed = 0;
    for (const p of planned) {
      try {
        const res = await api.post(`/brand-slides/${p.id}/send`, {
          brand_id:      parseInt(brandId),
          device_names:  p.device_names,
          group_ids:     p.group_ids.map(Number),
          target_folder: p.target_folder,
        });
        totalSent   += res.data.success || 0;
        totalFailed += res.data.failed  || 0;
        onSendComplete(p.id, res.data.send_targets);
      } catch { totalFailed++; }
    }
    toast.dismiss(toastId);
    if (totalFailed === 0) toast.success(`✅ Sent to ${totalSent} TV${totalSent > 1 ? 's' : ''}!`);
    else toast.error(`⚠️ ${totalSent} sent, ${totalFailed} failed`);
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) return <div className="spinner" style={{ marginTop: 60 }} />;

  // ── Page-level menu items ─────────────────────────────────────────────────
  const selIds = [...selected];
  const hasSlides = slides.length > 0;

  return (
    <div className="page">
      <style>{`@keyframes sm-spin { to { transform: rotate(360deg) } }`}</style>

      {/* ── Header ── */}
      <div className="page-header" style={{ marginBottom: 24 }}>
        <div>
          {/* Back where this kind of slide is used, not where the page
              happens to sit in the menu. */}
          {isTemplateMode ? (
            <BrandNav brandId={brandId} subPath="slides" backLabel="Info Screens"
              backPath={`/brands/${brandId}/info-screens`} backKey="info-screens" />
          ) : (
            <BrandNav brandId={brandId} subPath="slides" backLabel="Menu Manager"
              backPath={`/brands/${brandId}/menu`} backKey="menu" />
          )}
          <h1 className="page-title">
            {brand?.name}
            <span style={{ fontWeight: 400, color: '#999', fontSize: 16, marginLeft: 8 }}>
              — {isTemplateMode ? 'Info Screen Templates' : 'Menu Slides'}
            </span>
          </h1>

          {/* ── Which kind am I editing ── */}
          <div style={{ display: 'inline-flex', border: '1.5px solid #E0E0E0', borderRadius: 10, overflow: 'hidden', marginTop: 10 }}>
            {[
              { k: 'products', icon: '🎨', label: 'Menu Slides',  hint: 'Rendered to a picture and sent to a TV' },
              { k: 'doorsign', icon: '🪧', label: 'Info Screens', hint: 'Published as a live page by an Info Screen' },
            ].map(t => (
              <button key={t.k} onClick={() => setKind(t.k)} title={t.hint}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px',
                         border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700,
                         background: kind === t.k ? '#1565C0' : '#fff',
                         color: kind === t.k ? '#fff' : '#666' }}>
                <span style={{ fontSize: 14 }}>{t.icon}</span>{t.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Select-all checkbox */}
          {hasSlides && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', userSelect: 'none' }}
              title={allSelected ? 'Deselect all' : 'Select all'}>
              <div onClick={toggleSelectAll}
                style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${allSelected || someSelected ? '#2196F3' : '#BDBDBD'}`, background: allSelected ? '#2196F3' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s', cursor: 'pointer', flexShrink: 0 }}>
                {allSelected && <span style={{ color: '#fff', fontSize: 12, fontWeight: 700, lineHeight: 1 }}>✓</span>}
                {someSelected && !allSelected && <span style={{ width: 10, height: 2, background: '#2196F3', borderRadius: 1, display: 'block' }} />}
              </div>
              {selected.size > 0 && <span style={{ fontSize: 12, color: '#666', fontWeight: 500 }}>{selected.size} selected</span>}
            </label>
          )}

          {/* AR/EN lang toggle */}
          {hasSlides && (
            <div style={{ display: 'flex', border: '1.5px solid #E0E0E0', borderRadius: 8, overflow: 'hidden' }}>
              {['ar','en'].map(l => (
                <button key={l} onClick={() => setLang(l)}
                  style={{ padding: '5px 10px', border: 'none', background: lang === l ? '#1565C0' : '#fff', color: lang === l ? '#fff' : '#555', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
          )}

          {/* ☰ Page hamburger menu */}
          <div style={{ position: 'relative' }} ref={pageMenuRef}>
            <button onClick={() => setShowPageMenu(p => !p)}
              style={{ height: 36, width: 36, borderRadius: 9, border: '1.5px solid #E0E0E0', background: showPageMenu ? '#E3F2FD' : '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#444' }}>
              <IcoHamburger />
            </button>
            {showPageMenu && (
              <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 1000, background: '#fff', borderRadius: 12, boxShadow: '0 6px 28px rgba(0,0,0,0.16)', border: '1px solid #E8E8E8', minWidth: 210, padding: '6px 0' }}>
                {/* + New */}
                <button onClick={() => { setShowPageMenu(false); handleNew(); }} style={pageMenuItemStyle()}>
                  <span style={{ fontSize: 15 }}>➕</span> {isTemplateMode ? 'New Template' : 'New Slide'}
                </button>

                <div style={{ height: 1, background: '#F0F0F0', margin: '4px 0' }} />

                {/* Update / Download / To TV are about a rendered picture
                    reaching a TV, which is not how an info screen works. */}
                {hasSlides && !isTemplateMode && (
                  <button onClick={selected.size > 0 ? handleUpdateSelected : handleUpdateAll}
                    disabled={updating}
                    style={pageMenuItemStyle(false, updating)}>
                    <span style={{ fontSize: 15 }}>🔄</span>
                    {selected.size > 0 ? `Update (${selected.size} selected)` : 'Update All'}
                  </button>
                )}

                {/* Download — all or selected */}
                {hasSlides && !isTemplateMode && (
                  <button onClick={handleBulkDownload} style={pageMenuItemStyle()}>
                    <span style={{ fontSize: 15 }}>⬇️</span>
                    {selected.size > 0 ? `Download (${selected.size} selected)` : 'Download All'}
                  </button>
                )}

                {/* To TV — last choice */}
                {hasSlides && !isTemplateMode && (
                  <button onClick={() => handleQuickSend(selected.size > 0 ? selIds : slides.map(s => s.id))}
                    style={pageMenuItemStyle()}>
                    <span style={{ fontSize: 15 }}>📺</span>
                    {selected.size > 0 ? `To TV (${selected.size} selected)` : 'To TV (all)'}
                    <span style={{ fontSize: 10, color: '#999', marginLeft: 'auto' }}>last choice</span>
                  </button>
                )}

                <div style={{ height: 1, background: '#F0F0F0', margin: '4px 0' }} />

                {/* Backup */}
                {hasSlides && (
                  <button onClick={handleBulkBackup} style={pageMenuItemStyle()}>
                    <span style={{ fontSize: 15 }}>💾</span>
                    {selected.size > 0 ? `Backup (${selected.size} selected)` : 'Backup All'}
                  </button>
                )}

                {/* Restore */}
                <button onClick={() => { setShowPageMenu(false); restoreFileRef.current?.click(); }} style={pageMenuItemStyle()}>
                  <span style={{ fontSize: 15 }}>♻️</span> Restore from backup
                </button>

                {/* Delete selected */}
                {selected.size > 0 && (
                  <>
                    <div style={{ height: 1, background: '#F0F0F0', margin: '4px 0' }} />
                    <button onClick={() => { setShowPageMenu(false); handleBulkDelete(); }}
                      style={pageMenuItemStyle(true)}>
                      <span style={{ fontSize: 15 }}>🗑</span> Delete ({selected.size} selected)
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Slide grid ── */}
      {slides.length === 0 ? (
        <div className="empty-state card">
          <div style={{ fontSize: 52, marginBottom: 14 }}>{isTemplateMode ? '🪧' : '🎨'}</div>
          <h3>{isTemplateMode ? 'No templates yet' : 'No slides yet'}</h3>
          <p>{isTemplateMode
            ? 'A template is the design an Info Screen publishes — build one, then point screens at it'
            : 'Create your first slide to start building your menu board'}</p>
          <button
            style={{ marginTop: 16, height: 40, paddingLeft: 18, paddingRight: 20, borderRadius: 8, border: 'none', background: '#2196F3', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 8 }}
            onClick={handleNew}>
            <IcoPlus /> {isTemplateMode ? 'New Template' : 'New Slide'}
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 22 }}>
          {slides.map(slide => (
            <ThumbCard
              key={slide.id}
              slide={slide}
              brandId={brandId}
              navigate={navigate}
              isTemplate={isTemplateMode}
              backFrom={backFrom}
              selected={selected}
              onToggleSelect={toggleSelect}
              renaming={renaming}
              renameVal={renameVal}
              renameRef={renameRef}
              onRenameStart={startRename}
              onRenameChange={setRenameVal}
              onRenameSubmit={submitRename}
              onRenameCancel={cancelRename}
              onPreview={(id) => setPreviewSlideId(id)}
              onUpdate={handleUpdateOne}
              onSendTV={(id) => openSendModal([id])}
              onDownload={handleDownload}
              onBackup={handleBackup}
              onRestore={(id) => { setRestoreTargetId(id); restoreCardRef.current?.click(); }}
              onDuplicate={handleDuplicate}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      <div style={{ height: 40 }} />

      {/* ── Send-to-TV modal ── */}
      {sendModal && (
        <SendToTVModal
          slides={slides}
          slideIds={sendModal.slideIds}
          brandId={brandId}
          onClose={() => setSendModal(null)}
          onSent={onSendComplete}
        />
      )}

      {/* ── Update progress overlay ── */}
      {updating && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.72)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20 }}>
          <div style={{ width: 56, height: 56, border: '5px solid rgba(255,255,255,0.15)', borderTopColor: '#4CAF50', borderRadius: '50%', animation: 'sm-spin 0.8s linear infinite' }} />
          <div style={{ color: '#fff', fontSize: 20, fontWeight: 700 }}>Updating Slides…</div>
          <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 15 }}>
            {updateProgress.current > 0
              ? slides.find(s => s.id === [...selected][updateProgress.current - 1])?.slide_name || `Slide ${updateProgress.current}`
              : 'Loading menu data…'}
          </div>
          <div style={{ width: 280, height: 7, background: 'rgba(255,255,255,0.12)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ height: '100%', background: '#4CAF50', borderRadius: 4, width: `${updateProgress.total > 0 ? (updateProgress.current/updateProgress.total)*100 : 0}%`, transition: 'width 0.35s ease' }} />
          </div>
          <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13 }}>{updateProgress.current} / {updateProgress.total}</div>
          <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, marginTop: 4 }}>Please wait — do not close this tab</div>
        </div>
      )}

      {/* ── ZIP download progress overlay ── */}
      {zipProgress && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.72)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20 }}>
          <div style={{ width: 56, height: 56, border: '5px solid rgba(255,255,255,0.15)', borderTopColor: '#2196F3', borderRadius: '50%', animation: 'sm-spin 0.8s linear infinite' }} />
          <div style={{ color: '#fff', fontSize: 20, fontWeight: 700 }}>
            {zipProgress.phase === 'zipping' ? 'Creating ZIP…' : 'Preparing Slides…'}
          </div>
          <div style={{ width: 280, height: 7, background: 'rgba(255,255,255,0.12)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ height: '100%', background: '#2196F3', borderRadius: 4, width: `${zipProgress.total > 0 ? (zipProgress.current / zipProgress.total) * 100 : 0}%`, transition: 'width 0.3s ease' }} />
          </div>
          <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 14 }}>
            {zipProgress.phase === 'zipping' ? 'Compressing…' : `${zipProgress.current} / ${zipProgress.total} slides`}
          </div>
          <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>Please wait — do not close this tab</div>
        </div>
      )}

      {/* ── Hidden file inputs for restore ── */}
      <input ref={restoreFileRef} type="file" accept=".json,.zip" multiple style={{ display: 'none' }}
        onChange={e => { handleRestoreFiles(Array.from(e.target.files)); e.target.value = ''; }} />
      <input ref={restoreCardRef} type="file" accept=".json" style={{ display: 'none' }}
        onChange={e => { if (e.target.files[0] && restoreTargetId) handleRestoreToSlide(restoreTargetId, e.target.files[0]); e.target.value = ''; setRestoreTargetId(null); }} />

      {/* ── Fullscreen slide preview — image OR video depending on jpeg_path ── */}
      {/* 2026-05-16: video slides save the composed MP4 into jpeg_path (legacy
          column name). The preview now branches on the file extension so video
          slides render in a <video> element with native player controls. */}
      {previewSlideId && (() => {
        const ps = slides.find(s => s.id === previewSlideId);
        if (!ps) return null;
        const src    = ps.jpeg_path ? `${ps.jpeg_path}?v=${new Date(ps.updated_at || 0).getTime()}` : '';
        const isMp4  = ps.jpeg_path && /\.mp4(\?.*)?$/i.test(ps.jpeg_path);
        return (
          <div onClick={() => setPreviewSlideId(null)}
            style={{ position: 'fixed', inset: 0, zIndex: 99999, background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <button onClick={e => { e.stopPropagation(); setPreviewSlideId(null); }}
              style={{ position: 'absolute', top: 16, right: 16, padding: '8px 14px', borderRadius: 8, border: '1.5px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', backdropFilter: 'blur(8px)' }}>
              ✕ Close (Esc)
            </button>
            <div style={{ position: 'absolute', top: 18, left: 18, color: 'rgba(255,255,255,0.7)', fontSize: 12, fontWeight: 500 }}>
              {ps.slide_name || '(unnamed)'} {ps.orientation === 'V' ? '· Vertical' : '· Horizontal'} {isMp4 ? '· 🎬 Video' : ''}
            </div>
            {!src ? (
              <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, padding: 30, color: '#666', fontSize: 14, cursor: 'default' }}>
                This slide hasn't been saved yet — open the editor and click <b>Save</b> first.
              </div>
            ) : isMp4 ? (
              <video src={src} controls autoPlay loop onClick={e => e.stopPropagation()}
                style={{ maxWidth: 'calc(100vw - 40px)', maxHeight: 'calc(100vh - 80px)', borderRadius: 6, boxShadow: '0 12px 48px rgba(0,0,0,0.6)', cursor: 'default', background: '#000' }} />
            ) : (
              <img src={src} alt={ps.slide_name} onClick={e => e.stopPropagation()}
                style={{ maxWidth: 'calc(100vw - 40px)', maxHeight: 'calc(100vh - 80px)', borderRadius: 6, boxShadow: '0 12px 48px rgba(0,0,0,0.6)', cursor: 'default', objectFit: 'contain' }} />
            )}
          </div>
        );
      })()}
    </div>
  );
}

function pageMenuItemStyle(danger = false, disabled = false) {
  return {
    display: 'flex', alignItems: 'center', gap: 9,
    width: '100%', padding: '9px 14px', border: 'none',
    background: 'transparent', cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 13, color: disabled ? '#bbb' : (danger ? '#C62828' : '#333'),
    textAlign: 'left', opacity: disabled ? 0.5 : 1,
  };
}
